import {
  Installment,
  PaymentPlanId,
  PaymentPlanDisabledReason,
} from '../common/models/order.model';

export interface SystemAvailabilityResponse {
  maintenanceMode: boolean;
}

export interface ValidateOrderValueResponse {
  status: boolean;
  message: string;
  currency: string;
  redirectUrl: string;
  messageArabic: string;
  minimumOrderValue: number;
  maximumOrderValue: number;
  maxOrderValueExceeded: boolean;
}

export interface PaymentPlansResponse {
  name: string;
  status: string;
  id: PaymentPlanId;
  arabicName: string;
  monthsPeriod: number;
  duePaymentDay: number;
  downPayPercentage: number;
  installment: Installment[];
  disabledReason?: PaymentPlanDisabledReason | null;
}

export interface Card {
  id: string;
  status: string;
  cardType: string;
  holderName: string;
  expiryYear: number;
  key: string | null;
  expiryMonth: number;
  defaultCard: boolean;
  orderId: string | null;
  mastercardTokenExists: boolean;
  myFatoorahTokenExists: boolean;
  maskedCreditCardNumber: string;
  merchantRedirectUrl: string | null;
}

export interface CnetValidationResponse {
  result: string;
  orderId: number;
  civilId: string;
  riskType: string;
  talyScore: number;
  creditScore: number;
  creditLimit: number;
  rejectionReason: string;
  errorMessage: string | null;
}

export interface CreateSessionRequest {
  orderId: number;
  paymentPlanId: PaymentPlanId;
}

export interface MpgsCheckoutSessionResponse {
  result: string;
  merchant: string;
  checkoutMode: string;
  successIndicator: string;
  masterCardBaseUrl: string;
  masterCardCheckoutJSUrl: string;
  session: { id: string; updateStatus: string; version: string };
}

export type MpgsFailureCode =
  | 'DECLINED'
  | 'EXPIRED_CARD'
  | 'TIMED_OUT'
  | 'ACQUIRER_SYSTEM_ERROR'
  | 'UNSPECIFIED_FAILURE'
  | 'UNKNOWN';

/** The backend contract is not settled: it may send the reason as `gatewayCode` or `status`. */
export interface MpgsSessionFailureResponse {
  gatewayCode?: MpgsFailureCode;
  status?: MpgsFailureCode;
}

export const MPGS_GATEWAY_CODE_MESSAGES: Record<string, string> = {
  DECLINED: 'CHECKOUT.ADD_CARD_DECLINED',
  EXPIRED_CARD: 'CHECKOUT.ADD_CARD_EXPIRED',
  TIMED_OUT: 'CHECKOUT.ADD_CARD_BANK_TIMED_OUT',
  ACQUIRER_SYSTEM_ERROR: 'CHECKOUT.ADD_CARD_BANK_UNAVAILABLE',
  UNSPECIFIED_FAILURE: 'CHECKOUT.ADD_CARD_UNSPECIFIED_FAILURE',
  UNKNOWN: 'CHECKOUT.ADD_CARD_RESULT_UNKNOWN',
};

export const GATEWAY_TOKEN_MISSING_CODES = ['GATEWAY_TOKEN_MISSING', 'MPGS_NO_TOKEN'];

export const MPGS_CARD_NOT_CAPTURED = 'T847';
export const MPGS_CARD_REGISTRATION_FAILED = 'T849';
export const MPGS_CARD_COMPLETION_IN_PROGRESS = 'T848';

// TODO: Uncomment to enable MyFatoorah
// export interface CreateSessionResponse {
//   Message: string;
//   SessionId: string;
//   IsSuccess: boolean;
//   ValidationErrors: string | null;
//   Session: {
//     id: string;
//   };
//   Data: {
//     SessionId: string;
//     Card: string;
//     IsUsed: boolean;
//     SessionExpiry: string;
//     EncryptionKey: string;
//     OperationType: string;
//     TransactionResult: string;
//     Order: {
//       Amount: number;
//       Currency: string;
//       ExternalIdentifier: string;
//     };
//     Customer: {
//       Name: string;
//       Email: string;
//       Cards: string;
//       Mobile: string;
//       Reference: string;
//     };
//   };
// }

export interface OrderProcessRequest {
  orderToken: string;
  paymentType: string;
  paymentMethod: string;
  creditCardId?: string;
  paymentPlanId: PaymentPlanId;
}

export interface MyFatoorahConfig {
  sessionId: string;
  callback: (response: any) => void;
  containerId: string;
  shouldHandlePaymentUrl: boolean;
}

export interface paymentCallbackRequest {
  isSuccess: boolean;
  sessionId: string;
}

export type VerifyCreditCardV2Response =
  | string
  | {
      url: string;
      redirectUrl: string;
      id: string;
      creditCardId: string;
      userCreditCardId: string;
      status: string;
      paymentStatus: string;
    };

export interface ErrorMessage {
  enMessageTitle: string;
  arMessageTitle: string;
  enMessageDescription: string;
  arMessageDescription: string;
}

export interface CnetAuthenticationRequest {
  civilId: string;
  requestId: string;
}

export interface RequestStatusRequest {
  requestId: string;
  nationalId?: string;
  civilId?: string;
  uniqueId?: string;
}

export interface VerifyNationalIdRequest {
  nationalId?: string;
  requestId: string;
  deepLink?: boolean;
}

export interface AuthenticateCivilIdResponse {
  code: string | null;
  message: string | null;
  returnUrl: string | null;
  reference: string | null;
  operationId: string | null;
  redirectUserTo: string | null;
}

export interface RequestCivilIdStatusResponse {
  civilId: string;
  reference: string;
  details: string | null;
  paciTagId: string | null;
  status: 'PENDING' | 'APPROVED' | 'DECLINED';
}

export interface PayLaterRequest {
  orderToken: string;
  creditCardId: string;
  paymentPlanId: PaymentPlanId;
}

export interface PayLaterResponse {
  orderId: number;
  orderToken: string;
  authNumber: string;
  paymentDate: string;
  orderStatus: string;
  redirectUrl: string;
  retryAllowed: boolean;
  merchantOrderId: string;
}

export interface UnlockAccountRequest {
  flowType: string;
  civilId: string;
  clientChannel: string;
}
export interface lockAccountStatusRequest {
  flowSessionId: string;
}
export interface completeAccountFlowRequest {
  flowSessionId: string;
}

export enum ChangePhoneNumberScreen {
  OTP = 'otp',
  UPDATE_PHONE_NUMBER = 'update-phone-number',
  CIVIL_ID = 'civil-id',
  CHECKING_STATUS = 'checking-status',
  SUCCESS = 'success',
}

export interface ChangePhoneNumberAuthenticateRequest {
  flowType: string;
  civilId: string;
  clientChannel: string;
}

export interface ChangePhoneNumberStatusRequest {
  flowSessionId: string;
}

export interface ChangePhoneNumberLoginRequest {
  countryCode: string;
  mobileNumber: string;
  flowSessionId: string;
}

export interface ChangePhoneNumberOtpRequest {
  otpValue: string;
  flowSessionId: string;
}

export interface ResendChangeNumberOtpRequest {
  flowSessionId: string;
}

export interface StandaloneInstallmentPaymentRequest {
  installmentIds?: number[];
}

export interface StandaloneApplePayPaymentRequest extends StandaloneInstallmentPaymentRequest {
  paymentData: string;
  paymentToken: string;
  applePaymentMethod: string;
  transactionIdentifier: string;
}

export interface PolicyAcceptanceRequest {
  orderId: string;
}
